import { css, StyleSheet } from 'aphrodite';
import { startOfDay } from 'date-fns';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Calendar } from '../../../../web/components-v2/Calendar';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { TimeInput } from '../../../../web/components-v2/TimeInput';
import {
	combineDayAndTime,
	formatSupportTime,
	getSupportTimeLimit,
	getSupportTimePresets,
	SUPPORT_WORKDAY_START_HOUR,
} from './supportTimePresets';

interface SupportTimePickerProps {
	value: Date | null;
	onChange: (value: Date | null) => void;
	/** Passed in so the presets and the limits read from one clock. */
	now: Date;
}

const DEFAULT_CUSTOM_TIME = `${String(SUPPORT_WORKDAY_START_HOUR).padStart(2, '0')}:00`;

/**
 * The "pick a time" control the desk shares between snooze and deferred sends: a short list of presets
 * and a custom date and time. It only picks; the caller validates and submits.
 */
export const SupportTimePicker: React.FC<SupportTimePickerProps> = ({ value, onChange, now }) => {
	const presets = getSupportTimePresets(now);
	const selectedPresetId = presets.find(preset => preset.at.getTime() === value?.getTime())?.id;
	const [isCustom, setIsCustom] = React.useState(() => !!value && !selectedPresetId);
	const [customDay, setCustomDay] = React.useState<Date | undefined>(() => value ?? undefined);
	const [customTime, setCustomTime] = React.useState(DEFAULT_CUSTOM_TIME);

	const commitCustom = (day: Date | undefined, time: string) => {
		onChange(day ? combineDayAndTime(day, time) : null);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div role='radiogroup' aria-label='Presets' className={css(styleSheet.presets)}>
				{presets.map(preset => {
					const selected = !isCustom && preset.id === selectedPresetId;
					return (
						<Button
							key={preset.id}
							type='button'
							variant='ghost'
							size='sm'
							role='radio'
							aria-checked={selected}
							styles={[styleSheet.preset, ...(selected ? [styleSheet.presetSelected] : [])]}
							onClick={() => {
								setIsCustom(false);
								onChange(preset.at);
							}}
						>
							<span>{preset.label}</span>
							<span className={css(styleSheet.presetTime)}>{formatSupportTime(preset.at, now)}</span>
						</Button>
					);
				})}
				<Button
					type='button'
					variant='ghost'
					size='sm'
					role='radio'
					aria-checked={isCustom}
					styles={[styleSheet.preset, ...(isCustom ? [styleSheet.presetSelected] : [])]}
					onClick={() => {
						setIsCustom(true);
						commitCustom(customDay, customTime);
					}}
				>
					Pick date &amp; time…
				</Button>
			</div>
			{isCustom && (
				<div className={css(styleSheet.custom)}>
					<Calendar
						mode='single'
						selected={customDay}
						defaultMonth={customDay ?? now}
						disabled={[{ before: startOfDay(now) }, { after: getSupportTimeLimit(now) }]}
						onSelect={day => {
							setCustomDay(day);
							commitCustom(day, customTime);
						}}
					/>
					<TimeInput
						value={customTime}
						onChange={time => {
							setCustomTime(time);
							commitCustom(customDay, time);
						}}
					/>
				</div>
			)}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.space[8],
	},
	presets: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.space[4],
	},
	preset: {
		justifyContent: 'space-between',
		gap: THEME.space[16],
		width: '100%',
	},
	presetSelected: {
		backgroundColor: THEME.colors.primary.light,
	},
	presetTime: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	custom: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.space[8],
	},
});
